/***************************************************************************
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import {
  Heading,
  Item,
  Key,
  Picker,
  Text,
  ToggleButton,
} from '@adobe/react-spectrum'
import { useAtom } from 'jotai'
import { map } from 'lodash-es'

import { ModelInputAnimationStatus } from '@a3d-viewer/viewer'
import { FormItem } from '../components/FormItem'
import { viewerAtom } from '../config'

import Pause from '@spectrum-icons/workflow/Pause'
import Play from '@spectrum-icons/workflow/Play'

function AnimationsPanel() {
  const [atom, setAtom] = useAtom(viewerAtom)

  const handleAnimationSelected = (key: Key) => {
    const animation = key as string
    setAtom({
      ...atom,
      model: {
        ...atom.model,
        selectedAnimation: animation,
      },
    })
  }

  const handleStatusUpdate = (value: ModelInputAnimationStatus) => {
    setAtom({
      ...atom,
      model: {
        ...atom.model,
        animationStatus: value,
      },
    })
  }

  return (
    <>
      <Heading UNSAFE_className="my-0">Animations</Heading>
      <FormItem>
        <Picker
          label="Choose an animation"
          onSelectionChange={handleAnimationSelected}
          selectedKey={atom.model.selectedAnimation}
          width={'100%'}
        >
          {map(atom.model.animations, (animation) => (
            <Item key={animation}>{animation}</Item>
          ))}
        </Picker>
      </FormItem>
      <FormItem>
        <ToggleButton
          isSelected={
            atom.model.animationStatus === ModelInputAnimationStatus.PLAY
          }
          onPress={() => handleStatusUpdate(ModelInputAnimationStatus.PLAY)}
          aria-label="Play"
        >
          <Play />
          <Text>Play</Text>
        </ToggleButton>
        <ToggleButton
          isSelected={
            atom.model.animationStatus === ModelInputAnimationStatus.PAUSE
          }
          onPress={() => handleStatusUpdate(ModelInputAnimationStatus.PAUSE)}
          aria-label="Pause"
        >
          <Pause />
          <Text>Pause</Text>
        </ToggleButton>
      </FormItem>
    </>
  )
}

export { AnimationsPanel }
